<template>
  <div>
    <!-- Tabs -->
    <div class="tabs mb-3">
      <ul>
        <li 
          @click="active = 'wall_details'"
          :class="{ 'is-active': active == 'wall_details' }">
          <a>Wall Data</a>
        </li>
        <li 
          @click="active = 'rail_details'"
          :class="{ 'is-active': active == 'rail_details' }">
          <a>Railing Data</a>
        </li>        
        <li 
          v-if="!firstLoad && estimation.rfq.drawing_highlights &&
            estimation.rfq.drawing_highlights.length > 0 && 
            estimation.rfq.drawing_highlights[0].is_approved"
          @click="active = 'drawing_highlights'"
          :class="{ 'is-active': active == 'drawing_highlights' }">
          <a>Drawing Highlights</a>
        </li>
        <li 
          v-if="!firstLoad && (wallDetails.length > 0 || railDetails.length > 0)"
          @click="active = 'summary'"
          :class="{ 'is-active': active == 'summary' }">
          <a>Summary</a>
        </li>
        <li 
          v-if="!firstLoad && (wallDetails.length > 0 || railDetails.length > 0)"
          @click="active = 'proposal_preview'"
          :class="{ 'is-active': active == 'proposal_preview' }">
          <a>Proposal Preview</a>
        </li>
        <li 
          v-if="!firstLoad && (wallDetails.length > 0 || railDetails.length > 0)"
          @click="active = 'retrofit_savings'"
          :class="{ 'is-active': active == 'retrofit_savings' }">
          <a>Retrofit Energy Savings</a>
        </li>
        <li 
          @click="active = 'environmental_impact'"
          :class="{ 'is-active': active == 'environmental_impact' }">
          <a>Environmental Impact</a>
        </li>
        <template v-if="!summaryFirstLoad">
          <li 
            v-for="summaryTab in summaryTabs"
            :key="summaryTab"
            class="capitalize"
            :class="{ 'is-active': active == summaryTab }"
            @click="active = summaryTab">
            <a>{{ summaryTab.replaceAll("_", " ") }}</a>
          </li>
        </template>
      </ul>
    </div>

    <!-- Last saved info for wall details -->
    <div
      v-if="!firstLoad"
      v-show="active == 'wall_details'" 
      class="estimation__details pb-3">
      <v-fade-transition mode="out-in">
        <div v-if="draftSavedAt" class="mb-2 has-text-grey is-size-7">
          Draft automatically saved at {{ formatDate(draftSavedAt) }}
        </div>
      </v-fade-transition>
      <div :key="estimation.id" id="wall-spreadsheet">
      </div>
    </div>

    <!-- Last saved info for rail details -->
    <div
      v-if="!firstLoad"
      v-show="active == 'rail_details'" 
      class="estimation__details pb-3">
      <v-fade-transition mode="out-in">
        <div v-if="draftSavedAt" class="mb-2 has-text-grey is-size-7">
          Draft automatically saved at {{ formatDate(draftSavedAt) }}
        </div>
      </v-fade-transition>
      <div :key="estimation.id" id="rail-spreadsheet">
      </div>
    </div>

    <!-- Drawing Highlights -->
    <div
      v-if="active == 'drawing_highlights'" 
      class="estimation__details pb-3"
    >
      <drawing-highlights 
        :drawing-highlight="estimation.rfq.drawing_highlights[0]"
      />
    </div>

    <!-- Summary -->
    <div 
      v-if="active=='summary' && !summaryFirstLoad && !firstLoad"
      class="px-3 py-2"
    >
      <div class="field">
        <div 
          class="is-size-6 mb-2 has-text-weight-bold">
          Number of Runs
        </div>
        <div class="flex flex--row flex--align-center">
          <v-text-field 
            required
            :disabled="!editable"
            type="number"
            placeholder="Number of Packing and Delivery Runs"
            v-model="estimation.no_of_runs"
            @blur="update('no_of_runs', estimation.no_of_runs)"
            @keyup.enter.native="update('no_of_runs', estimation.no_of_runs)"/>
          <!-- <button
            :disabled="savingParams || !editable"
            @click="update('no_of_runs', estimation.no_of_runs)" 
            class="button ml-2"
            :class="{ 'is-loading': savingParams }">
            <span class="icon has-text-grey">
              <i class="fas fa-save"></i>
            </span>
          </button> -->
        </div>
        <div class="help">
          Estimated to be 
          <span class="has-text-weight-bold">
            {{ Math.ceil(estimation.total_raw_area / 5000) }}
          </span> based on a total area of {{ estimation.total_raw_area | commasep(0) }} SQFT
        </div>
      </div>

      <hr v-if="wallDetails.length > 0 || railDetails.length > 0"/>
        
      <template 
        v-if="Object.keys(estimationSummary).includes('by_material') && 
          estimationSummary['by_material'].length > 0">
        <div 
          class="is-size-6 mb-2 has-text-weight-bold">
          Summary of Price by Material
        </div>

        <table
          class="table is-fullwidth is-hoverable has-small-font mb-0 summary-table">
          <thead>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'product_master_id'"
                  :key="label">
                  {{ label.replaceAll("_", " ") }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr 
              class="v-centered"
              v-for="(row, i) in estimationSummary['by_material']"
              :key="i">
              <template
                v-for="(rowValue, rowLabel) in row">
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="!['product_master_id', 'non_active_unit_price_adj', 
                    'active_unit_price_adj', 'product_image'].includes(rowLabel)"
                  :key="rowLabel">
                  {{ formatValue(rowValue, rowLabel) }}
                </td>
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="['non_active_unit_price_adj', 
                    'active_unit_price_adj'].includes(rowLabel)"
                  :key="rowLabel">
                  <div 
                    v-if="!(rowLabel == 'active_unit_price_adj' && row['product_brand'] == 'Cladify')" 
                    class="flex flex--row flex--align-center">
                    <v-text-field
                      class="flex--grow"
                      :disabled="!editable"
                      required
                      :value="rowValue"
                      @input="updateSummary('by_material', i, rowLabel, $event)"
                      @blur="adjustPrice(
                        'by_material', 
                        row, 
                        row[rowLabel], 
                        rowLabel == 'active_unit_price_adj')"
                      @keyup.enter.native="adjustPrice(
                        'by_material', 
                        row, 
                        row[rowLabel], 
                        rowLabel == 'active_unit_price_adj')"
                      :rules="[
                        v => typeof parseFloat(v) === 'number' || 'Please enter a number'
                      ]"/>
                    <!-- <button 
                      :disabled="savingSummary"
                      :class="{ 'is-loading': savingSummary }"
                      class="button ml-2"
                      @click="adjustPrice('by_material', 
                        row, 
                        row[rowLabel], 
                        rowLabel == 'active_unit_price_adj')">
                      <span 
                        class="icon is-small has-text-grey" 
                        title="save changes">
                        <i class="fas fa-save">
                        </i>
                      </span>
                    </button> -->
                  </div>
                </td>
                <td :key="rowLabel" v-if="rowLabel == 'product_image'">
                  <a 
                    class="flex flex--row flex--align-center"
                    @click.stop=""
                    :href="rowValue"
                    target="_blank"
                    v-if="rowValue"
                    tabindex="-1">
                    <img 
                      width="30px"
                      height="auto"
                      :src="rowValue"/>
                  </a>
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'product_master_id'"
                  :key="label">
                  {{ getTotal(label, estimationSummary['by_material'], 'summary') }}
                </th>
              </template>
            </tr>
          </tfoot>
        </table>

        <hr/>
      </template>

      <template 
        v-if="Object.keys(estimationSummary).includes('irregular_price_by_material') && 
          estimationSummary['irregular_price_by_material'].length > 0">
        <div 
          class="is-size-6 mb-2 has-text-weight-bold">
          Summary of Price by Irregular Material
        </div>

        <table
          class="table is-fullwidth is-hoverable has-small-font mb-0 summary-table">
          <thead>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['irregular_price_by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'product_master_id'"
                  :key="label">
                  {{ label.replaceAll("_", " ") }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr 
              class="v-centered"
              v-for="(row, i) in estimationSummary['irregular_price_by_material']"
              :key="i">
              <template
                v-for="(rowValue, rowLabel) in row">
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="!['product_master_id', 'unit_price_adjustment', 'product_image'].includes(rowLabel)"
                  :key="rowLabel">
                  {{ formatValue(rowValue, rowLabel) }}
                </td>
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="rowLabel == 'unit_price_adjustment'"
                  :key="rowLabel">
                  <div class="flex flex--row flex--align-center">
                    <v-text-field
                      class="flex--grow"
                      required
                      :disabled="!editable"
                      :value="rowValue"
                      @input="updateSummary(
                        'irregular_price_by_material', i, 'unit_price_adjustment', $event
                      )"
                      @blur="adjustPrice(
                        'irregular_cladding', row, row.unit_price_adjustment, false)"
                      @keyup.enter.native="adjustPrice(
                        'irregular_cladding', row, row.unit_price_adjustment, false)"
                      :rules="[
                        v => typeof parseFloat(v) === 'number' || 'Please enter a number'
                      ]"/>
                    <!-- <button 
                      :disabled="savingSummary"
                      :class="{ 'is-loading': savingSummary }"
                      class="button ml-2"
                      @click="adjustPrice(
                        'irregular_cladding', row, row.unit_price_adjustment, false
                      )">
                      <span 
                        class="icon is-small has-text-grey" 
                        title="save changes">
                        <i class="fas fa-save">
                        </i>
                      </span>
                    </button> -->
                  </div>
                </td>
                <td :key="rowLabel" v-if="rowLabel == 'product_image'">
                  <a 
                    class="flex flex--row flex--align-center"
                    @click.stop=""
                    :href="rowValue"
                    target="_blank"
                    v-if="rowValue"
                    tabindex="-1">
                    <img 
                      width="30px"
                      height="auto"
                      :src="rowValue"/>
                  </a>
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['irregular_price_by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'product_master_id'"
                  :key="label">
                  {{ getTotal(label, estimationSummary['irregular_price_by_material'], 'summary') }}
                </th>
              </template>
            </tr>
          </tfoot>
        </table>

        <hr/>
      </template>

      <template 
        v-if="estimationSummary['summary'].length > 0">
        <div  
          class="is-size-6 mb-2 has-text-weight-bold">
          Pricing Summary
        </div>

        <table
          class="table is-fullwidth is-hoverable has-small-font mb-0 summary-table">
          <thead>
            <tr>
              <template 
                v-for="(value, label) in sortedSummary[0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'product_master_id'"
                  :key="label">
                  {{ label.replaceAll("_", " ") }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, i) in sortedSummary">
              <tr 
                v-if="summaryRowsOrder.includes(row.pricing_summary)"
                class="v-centered"
                :key="i">
                <template
                  v-for="(rowValue, rowLabel) in row">
                  <td 
                    :class="columnClasses[rowLabel]"
                    :key="rowLabel">
                    {{ formatValue(rowValue, rowLabel) }}
                  </td>
                </template>
              </tr>
            </template>
            <tr class="subtotal-row">
              <td class="has-text-weight-bold">
                Subtotal
              </td>
              <template 
                v-for="(value, label) in sortedSummary[0]">
                <th  
                  :class="columnClasses[label]"
                  v-if="label != 'pricing_summary'"
                  :key="label">
                  {{ getTotal(
                      label, 
                      sortedSummary.filter(
                        d => summaryRowsOrder.includes(d.pricing_summary)
                      ),
                      'summary',
                      true
                    ) }}
                </th>
              </template>
            </tr>
            <tr v-if="railingSummary">
              <td 
                v-for="(rowValue, rowLabel) in railingSummary"
                :class="columnClasses[rowLabel]"
                :key="rowLabel">
                {{ formatValue(rowValue, rowLabel) }}
              </td>
            </tr>
            <tr v-if="railingSummary" class="subtotal-row">
              <td class="has-text-weight-bold">
                Subtotal
              </td>
              <template 
                v-for="(value, label) in railingSummary">
                <th  
                  :class="columnClasses[label]"
                  v-if="label != 'pricing_summary'"
                  :key="label + '-subtotal'">
                  {{ getTotal(
                      label, 
                      estimationSummary['summary'].filter(
                        d => summaryRowsOrder.includes(d.pricing_summary) || 
                        d.pricing_summary.includes('railing')
                      ),
                      'summary',
                      true
                    ) }}
                </th>
              </template>
            </tr>
            <tr v-if="batterySummary" class="v-centered">
              <template v-for="(rowValue, rowLabel) in batterySummary">
                <td 
                  v-if="!['system_size', 'average_value', 'average_actual_price'].includes(rowLabel)"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  {{ formatValue(rowValue, rowLabel) }}
                </td>
                <td 
                  v-if="rowLabel == 'average_actual_price'"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  <v-text-field
                    type="number" 
                    :disabled="!editable"
                    v-model="estimation.battery_unit_price"
                    @blur="update('battery_unit_price', $event, true)"
                    @keyup.enter.native="update('battery_unit_price', estimation.battery_unit_price, true)"/>
                </td>
                <td 
                  v-if="rowLabel == 'system_size'"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  <v-text-field
                    type="number" 
                    :disabled="!editable"
                    v-model="estimation.batteries_system_size_kw"
                    @blur="update('batteries_system_size_kw', $event, true)"
                    @keyup.enter.native="update('batteries_system_size_kw', estimation.batteries_system_size_kw, true)"/>
                </td>
                <td 
                  v-if="rowLabel == 'average_value'"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  <v-text-field
                    type="number" 
                    :disabled="!editable"
                    v-model="estimation.batteries_and_invertors_val"
                    @blur="update('batteries_and_invertors_val', $event, true)"
                    @keyup.enter.native="update('batteries_and_invertors_val', estimation.batteries_and_invertors_val, true)"/>
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['summary'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'product_master_id'"
                  :key="label">
                  {{ getTotal(label, estimationSummary['summary'], 'summary') }}
                </th>
              </template>
            </tr>
          </tfoot>
        </table>
        
      </template>

      <hr/>

      <div class="columns is-multiline">
        <template v-for="table in summaryTables">
          <div 
            v-if="estimationSummary[table.section] && 
              estimationSummary[table.section].length > 0"
            :key="table.section"
            :class="table.class">
            <div class="is-size-6 mb-2 has-text-weight-bold">
              {{ table.title }}
            </div>
            <table
              class="table is-hoverable has-small-font is-fullwidth mb-0 summary-table">
              <thead>
                <tr>
                  <template 
                    v-for="(value, label) in estimationSummary[table.section][0]">
                    <th  
                      class="capitalize"
                      :class="columnClasses[label]"
                      v-if="label != 'product_master_id'"
                      :key="label">
                      {{ label.replaceAll("_", " ") }}
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr 
                  class="v-centered"
                  v-for="(row, i) in estimationSummary[table.section]"
                  :key="i">
                  <template
                    v-for="(rowValue, rowLabel) in row">
                    <td 
                      :class="columnClasses[rowLabel]"
                      v-if="!['product_master_id', 'product_image'].includes(rowLabel)"
                      :key="rowLabel">
                      {{ formatValue(rowValue, rowLabel) }}
                    </td>
                    <td :key="rowLabel" v-if="rowLabel == 'product_image'">
                      <a 
                        class="flex flex--row flex--align-center"
                        @click.stop=""
                        :href="rowValue"
                        target="_blank"
                        v-if="rowValue"
                        tabindex="-1">
                        <img 
                          width="30px"
                          height="auto"
                          :src="rowValue"/>
                      </a>
                    </td>
                  </template>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <template 
                    v-for="(value, label) in estimationSummary[table.section][0]">
                    <th  
                      class="capitalize"
                      :class="columnClasses[label]"
                      v-if="label != 'product_master_id'"
                      :key="label">
                      {{ getTotal(label, estimationSummary[table.section], 'summary') }}
                    </th>
                  </template>
                </tr>
              </tfoot>
            </table>
          </div>
        </template>
      </div>

    </div>

    <!-- Proposal Preview -->
    <div 
      v-if="active == 'proposal_preview'"
      class="container pt-2 pb-5">
      <proposal-preview-mitrex 
        :estimation="estimation"
        :pricing-type="summaryParams.pricing_type"/>
    </div>

    <!-- Retrofit Energy Savings -->
    <div 
      v-if="active == 'retrofit_savings'"
      class="container pt-2 pb-5">
      <div 
        v-for="(varType, i) in retrofitVars" 
        :key="i">
        <label class="label">{{ varType.name }}</label>
        <div class="columns">
          <div 
            v-for="variable in varType.vars" 
            :key="variable.alias" 
            class="column">
            <label class="help capitalize mb-1 ml-2">
              {{ variable.alias }}
            </label>
            <v-text-field
              type="number" 
              :disabled="!editable"
              v-model="estimation.insulation[variable.field]"
              @blur="updateInsulationParam(
                estimation.insulation.id, variable.field, $event)"
              @keyup.enter.native="updateInsulationParam(
                estimation.insulation.id, variable.field, estimation.insulation[variable.field])"/>
          </div>
        </div>
      </div>
      <hr/>
      <retrofit-savings
        class="mt-3" 
        :rfq="{ c_estimation: estimation }"/>
    </div>

    <!-- Environmental Impact -->
    <div 
      v-if="active=='environmental_impact'"
      class="px-3 py-2">      
      <div class="is-size-6 mb-1 has-text-weight-bold">
        Greenhouse Gas Equivalencies
      </div>
      <div class="columns">
        <template v-for="ratio in ghgEquiv">
          <div class="column" :key="ratio">
            <label class="help capitalize mb-1 ml-2">
              {{ ratio.replaceAll("_", " ") }}
            </label>
            <v-text-field
              type="number" 
              :disabled="!editable"
              v-model="estimation[ratio]"
              @blur="update(ratio, $event)"
              @keyup.enter.native="update(ratio, estimation[ratio])"/>
          </div>
        </template>
      </div>

      <hr class="my-3"/>

      <div class="is-size-6 mb-1 has-text-weight-bold">
        Sun Hours
      </div>
      <div class="columns">
        <template v-for="elevation in sunHourFields">
          <div class="column" :key="elevation">
            <label class="help capitalize mb-1 ml-2">
              {{ elevation.replaceAll("_", " ") }}
            </label>
            <v-text-field
              type="number"
              :disabled="!editable"
              v-model="estimation[elevation]"
              @blur="update(elevation, $event)"
              @keyup.enter.native="update(elevation, estimation[elevation], true)"/>
          </div>
        </template>
      </div>

      <hr class="my-3"/>

      <div class="is-size-6 mb-1 has-text-weight-bold">
        Environmental Impact Output
      </div>
      <environmental-impact-summary 
        :estimation="estimation"
        :estimation-summary="estimationSummary"
        :sun-hour-fields="sunHourFields"/>

    </div>

    <!-- Other Summary Tabs -->
    <div v-if="!summaryFirstLoad && !firstLoad">
      <template 
        v-for="section in summaryTabs">
        <div 
          v-if="section != 'summary'"
          :key="`${section}-data`"
          class="px-3">
          <table
            v-show="section == active"
            class="table is-fullwidth is-hoverable has-small-font summary-table mb-5">
            <thead>
              <tr>
                <template 
                  v-for="(value, label) in estimationSummary[section][0]">
                  <th  
                    class="capitalize"
                    :class="columnClasses[label]"
                    v-if="label != 'product_master_id'"
                    :key="label">
                    {{ label.replaceAll("_", " ") }}
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr 
                class="v-centered"
                v-for="(row, i) in estimationSummary[section]"
                :key="i">
                <template
                  v-for="(rowValue, rowLabel) in row">
                  <td 
                    v-if="!['product_master_id', 'product_image'].includes(rowLabel)"
                    :class="columnClasses[rowLabel]"
                    :key="rowLabel">
                    {{ formatValue(rowValue, rowLabel) }}
                  </td>
                  <td :key="rowLabel" v-if="rowLabel == 'product_image'">
                    <a 
                      class="flex flex--row flex--align-center"
                      @click.stop=""
                      :href="rowValue"
                      target="_blank"
                      v-if="rowValue"
                      tabindex="-1">
                      <img 
                        width="30px"
                        height="auto"
                        :src="rowValue"/>
                    </a>
                  </td>
                </template>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <template 
                  v-for="(value, label) in estimationSummary[section][0]">
                  <th  
                    class="capitalize"
                    :class="columnClasses[label]"
                    v-if="label != 'product_master_id'"
                    :key="label">
                    {{ getTotal(label, estimationSummary[section], section) }}
                  </th>
                </template>
              </tr>
            </tfoot>
          </table>
        </div>

      </template>
    </div>

    <load-from-draft-modal 
      v-if="draft && draft.data" 
      v-model="loadFromDraftModal"
      @confirm="loadEstimationFromDraft"
    />
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import DrawingHighlights from '@/components/estimations/DrawingHighlights'
import EnvironmentalImpactSummary from '@/components/estimations/EnvironmentalImpactSummary'
import LoadFromDraftModal from '@/components/estimations/LoadFromDraftModal'
import ProposalPreviewMitrex from '@/components/proposalmaker/ProposalPreviewMitrex'
import RetrofitSavings from '@/components/proposalmaker/RetrofitSavings'

import { sunHourFields } from '@/data/estimation-data'
import { commonEstimationHelpers, newEstimationHelpers } from '@/mixins/helpers'
import { mapState, mapActions, mapMutations } from 'vuex'
import { parseJSON, format } from 'date-fns'

import jexcel from 'jexcel'
import 'jexcel/dist/jexcel.css'

export default {
  props: {
    editable: {
      type: Boolean
    },
    update: {
      type: Function
    },
    adjustPrice: {
      type: Function
    },
    updateInsulationParam: {
      type: Function
    },
    can: {
      type: Object
    },
    setSaving: {
      type: Function
    }
  },
  components: {
    VTextField, VFadeTransition, DrawingHighlights, ProposalPreviewMitrex,
    RetrofitSavings, EnvironmentalImpactSummary, LoadFromDraftModal
  },
  mixins: [commonEstimationHelpers, newEstimationHelpers],
  data() {
    return {
      active: 'wall_details',
      wallFields: [
        'id', 'elevation_id', 'estimation_item_id',
        'details', 'length_in', 'width_in', 'panels', 
        'area', 'cell_q', 'power', 'linear_type', 'linear_in'
      ],
      railFields: [
        'id', 'elevation_id', 'estimation_item_id', 'details', 'length_in', 'width_in',
        'panels', 'area', 'solar_panels', 'power', 'linear_in'
      ],
      parseDate: (date) => { return date ? parseJSON(date) : date },
      formatDate: (date) => format(date, "yyyy-MM-dd HH:mm:ss"),
      detailWidth: 500,
      columnClasses: {
        'sum_area_sqft': 'border--after',
        'total_cost': 'border--after',
        'unit_price_adjustment': 'border--before',
        'unit_price_adj': 'border--before border--after',
        'non_active_price': 'border--after',
        'adjusted_total_cost': 'border--before',
        'elec_proceeds_for_mitrex': 'border--before',
        'cladify_area_sqft': 'border--after',
        'cladify_cost': 'border--after',
        'power': 'border--before border--after',
        'system_size': 'border--before border--after',
        'system_capacity': 'border--before border--after',
        'annual_sun_hours': 'border--before',
        'railing_cost': 'border--before'
      },
      summaryRowsOrder: [
        'solar_wall_active', 'solar_wall_non_active', 
        'cladding', 'irregular'
      ],
      summaryTables: [
        { 
          section: 'price_by_material_cladding', 
          title: 'Summary of Price by Material and Product Brand', 
          class: 'column is-12' 
        },
        { 
          section: 'by_cladding_elevation', 
          title: 'Cladding Summary of Size by Elevation', 
          class: 'column is-12' 
        },
        { 
          section: 'by_railing_elevation', 
          title: 'Railing Summary of Size by Elevation', 
          class: 'column is-12'
        }
      ],
      retrofitVars: [
        {
          name: 'General',
          vars: [
            { alias: 'Years', field: 'years'}, 
            { alias: 'Current R', field: 'curr' }, 
            { alias: 'New R', field: 'newr' }, 
            { alias: 'Heating Degree Days (Farenheit)', field: 'hdd' }
          ] 
        },
        {
          name: 'Electricty',
          vars: [
            { alias: 'Electricity Cost per KWH', field: 'elec_fuel_cost' }, 
            { alias: 'Electricity Inflation', field: 'elec_fuel_inflation' }, 
            { alias: 'Electricity GHG/ Btu', field: 'elec_ghg_per_btu' }, 
            { alias: 'Electricity Heating Value', field: 'elec_heating_val' },
            { alias: 'Electricity Furnace Efficiency', field: 'elec_furnace_eff' }
          ]
        },
        {
          name: 'Natural Gas',
          vars: [
            { alias: 'Natural Gas Cost per therm', field: 'ng_fuel_cost' }, 
            { alias: 'Natural Gas Inflation', field: 'ng_fuel_inflation' }, 
            { alias: 'Natural Gas GHG/ Btu', field: 'ng_ghg_per_btu' }, 
            { alias: 'Natural Gas Heating Value', field: 'ng_heating_val' },
            { alias: 'Natural Gas Furnace Efficiency', field: 'ng_furnace_eff' }
          ]
        },
        {
          name: 'Fuel Oil',
          vars: [
            { alias: 'Fuel Oil Cost per Gallon', field: 'oil_fuel_cost' }, 
            { alias: 'Fuel Oil Inflation', field: 'oil_fuel_inflation' }, 
            { alias: 'Fuel Oil GHG/ Btu', field: 'oil_ghg_per_btu' }, 
            { alias: 'Fuel Oil Heating Value', field: 'oil_heating_val' },
            { alias: 'Fuel Oil Furnace Efficiency', field: 'oil_furnace_eff' }
          ]
        },
        {
          name: 'Propane',
          vars: [
            { alias: 'Propane Cost per Gallon', field: 'prop_fuel_cost' }, 
            { alias: 'Propane Inflation', field: 'prop_fuel_inflation' }, 
            { alias: 'Propane GHG/ Btu', field: 'prop_ghg_per_btu' }, 
            { alias: 'Propane Heating Value', field: 'prop_heating_val' },
            { alias: 'Propane Furnace Efficiency', field: 'prop_furnace_eff' }
          ]
        }
      ],
      ghgEquiv: [
        'cars_per_year_ratio', 'metric_tones_co2_ratio',
        'elec_homes_ratio', 'no_of_trees_ratio'
      ],
      sunHourFields,
      summaryTabsOrder: [
        'solar_wall_active', 'solar_wall_non_active', 
        'solar_railing_1100_1400', 'cladding', 'irregular_cladding'
      ],
      draftInterval: null,
      originalIds: [],
      loadFromDraftModal: false,
      loadedFromDraft: false
    }
  },
  computed: {
    ...mapState("cladifyEstimation", [
      "estimation", "loading", "firstLoad",
      "estimationSummary", "summaryLoading", "summaryFirstLoad",
      "summaryParams", "draft", "draftSavedAt"
    ]),
    ...mapState("elevations", {
      newElevations: "elevations"
    }),
    sortedSummary({ estimationSummary, summaryRowsOrder }) {
      return estimationSummary.summary.sort((a,b) => {
        return summaryRowsOrder.indexOf(
          a.pricing_summary
        ) - summaryRowsOrder.indexOf(
          b.pricing_summary
        )
      })
    },
    railingSummary({ estimationSummary }) {
      return estimationSummary.summary.filter(
        d => d.pricing_summary ? d.pricing_summary.includes('railing') : false
      )[0]
    },
    batterySummary({ estimationSummary }) {
      return estimationSummary.summary.filter(
        d => d.pricing_summary ? d.pricing_summary.includes('batteries') : false
      )[0]
    },
    summaryTabs({ estimationSummary, summaryTabsOrder }) {
      let summaryKeys = Object.keys(estimationSummary);
      return summaryTabsOrder.filter(tab => 
        summaryKeys.includes(tab) && estimationSummary[tab].length > 0
      )
    },
    wallDetails({ estimation, wallFields }) {
      let res = estimation.items.map(d => d.elevations).flat(1);
      res = res.filter(d => d.wall_railing == 'wall');
      let empty = new Array(wallFields.length);

      if (res.length > 0) {
        res.sort((a,b) => {
          if (a.order == b.order) {
            return a.id - b.id
          } else {
            return a.order - b.order
          }
        });
        res = res.map(d => {
          return wallFields.map(field => d[field]);
        });

        return res.length > 0 ? res : [empty];
      } else {
        return [empty];
      }
    },
    railDetails({ estimation, railFields }) {
      let res = estimation.items.map(d => d.elevations).flat(1);
      res = res.filter(d => d.wall_railing == 'railing');
      let empty = new Array(railFields.length);
      let widthInd = railFields.indexOf('width_in');
      empty[widthInd] = 43.2;

      if (res.length > 0) {
        res.sort((a,b) => {
          if (a.order == b.order) {
            return a.id - b.id
          } else {
            return a.order - b.order
          }
        });
        res = res.map(d => {
          return railFields.map(field => d[field]);
        });

        return res.length > 0 ? res : [empty];
      } else {
        return [empty];
      }
    },
    wallItems({ estimation }) {
      return estimation.items.filter(
        d => !d.materials.map(d => d.product_master.product_category_id).includes(20)
      ).map(d => {
        d.name = d.architecture_name;
        return d
      });
    },
    railItems({ estimation }) {
      return estimation.items.filter(
        d => d.materials.map(d => d.product_master.product_category_id).includes(20)
      ).map(d => {
        d.name = d.architecture_name;
        return d
      });
    },
    wallOptions({ detailWidth, wallTotCols, wallItems }) {
      let cellWidth = detailWidth / (wallTotCols - 1);

      return {
        data: this.wallDetails,
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        columns: [
          { type: 'text', width: `${cellWidth}px`, title: 'Id' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Elevation', 
            source: this.newElevations.map(d => {
              d.name = d.elevation_name; 
              return d
            })  
          },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Item', 
            source: wallItems,
            autocomplete: true
          },
          { type: 'text', width: `${cellWidth}px`, title: 'Details' },
          { type: 'text', width: `${cellWidth}px`, title: 'L"' },
          { type: 'text', width: `${cellWidth}px`, title: 'W"' },
          { type: 'number', width: `${cellWidth}px`, title: 'Quantity (Pieces)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Area (sqft)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Cell Q' },
          { type: 'number', width: `${cellWidth}px`, title: 'Power' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Linear Type', 
            source: ['opening', 'pre-assemble'] 
          },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Linear Inch' }
        ],
        oninsertrow: () => { this.wallSpreadsheet.hideColumn(0) },
        onafterchanges: (instance, changes) => {
          if (this.modifiedDetails.length == 0) {
            this.triggerAutoSave();
          }
          changes.forEach(change => {
            this.wallChanged(change);
          })
        },
        ondeleterow: (instance) => {
          if (this.modifiedDetails.length == 0) {
            this.triggerAutoSave();
          }
        },
        updateTable: (el, cell, x) => {
          const { wallFields } = this;
          const sqftInd = wallFields.indexOf('area'),
                cellInd = wallFields.indexOf('cell_q'),
                powerInd = wallFields.indexOf('power');

          if (!this.editable || !this.can.markAsReadyForPricing || this.estimation.is_ready_for_pricing) {
            cell.classList.add('readonly');
          } else if (
            [sqftInd, cellInd, powerInd].includes(+x)
          ) {
            cell.classList.add('readonly');
          }
        } 
      }
    },
    railOptions({ detailWidth, railTotCols, railItems }) {
      let cellWidth = detailWidth / (railTotCols - 1);

      return {
        data: this.railDetails,
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        columns: [
          { type: 'text', width: `${cellWidth}px`, title: 'Id' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Elevation', 
            source: this.newElevations.map(d => {
              d.name = d.elevation_name; 
              return d
            }) 
          },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Item', 
            source: railItems,
            autocomplete: true,
          },
          { type: 'text', width: `${cellWidth}px`, title: 'Details' },
          { type: 'text', width: `${cellWidth}px`, title: 'L"' },
          { type: 'text', width: `${cellWidth}px`, title: 'W"' },
          { type: 'number', width: `${cellWidth}px`, title: 'Quantity (Pieces)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Area (sqft)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Solar Panels' },
          { type: 'number', width: `${cellWidth}px`, title: 'Power' },
          { type: 'number', width: `${cellWidth}px`, title: 'Linear Inch' }
        ],
        oninsertrow: (instance, rowNumber) => { 
          this.railSpreadsheet.hideColumn(0);
          this.railInsertRow(instance, rowNumber);
        },
        onafterchanges: (instance, changes) => {
          if (this.modifiedDetails.length == 0) {
            this.triggerAutoSave();
          }
          changes.forEach(change => {
            this.railChanged(change);
          })
        },
        ondeleterow: (instance) => {
          if (this.modifiedDetails.length == 0) {
            this.triggerAutoSave();
          }
        },
        updateTable: (el, cell, x) => {
          const { railFields } = this;

          const sqftInd = railFields.indexOf('area'),
                panelInd = railFields.indexOf('solar_panels'),
                lfInd = railFields.indexOf('linear_in_str'),
                powerInd = railFields.indexOf('power');

          if (!this.editable || !this.can.markAsReadyForPricing || this.estimation.is_ready_for_pricing) {
            cell.classList.add('readonly');
          } else if (
            [sqftInd, panelInd, lfInd, powerInd].includes(+x)
          ) {
            cell.classList.add('readonly');
          }
        } 
      }
    },
    hasUnsavedDraft({ draft, lastManualSave }) {
      return this.parseDate(draft.created_on) > lastManualSave
    },
    workingFromDraft({ loadedFromDraft, hasUnsavedDraft }) {
      return loadedFromDraft && hasUnsavedDraft
    }
  },
  beforeDestroy() {
    clearInterval(this.draftInterval);
  },
  methods: {
    ...mapActions("cladifyEstimation", [
      "getEstimationSummary","getEstimation", "updateEstimation", 
      "addItemElevation", "updateItemElevation", "deleteItemElevation", 
      "updateElevationOrders", "saveDraft", "getDraft"
    ]),
    ...mapActions("elevations", ["getElevations"]),
    ...mapMutations("app", ["setNotification"]),
    initializeEstimation() {
      this.fetchDraft();
      this.setOriginalIds();
      this.checkElevations();
    },
    fetchDraft() {
      if (!this.estimation.is_ready_for_pricing) {
        this.getDraft(this.estimation.id).then(resp => {
          if (resp.data.data) {
            let wall = resp.data.data.wall;
            let rail = resp.data.data.rail;

            let diff = this.compareArr(wall, this.wallDetails).concat(
              this.compareArr(rail, this.railDetails)
            )
            if (this.hasUnsavedDraft && diff.length > 0) {
              this.loadFromDraftModal = true;
            }
          }
        })
      }
    },
    setOriginalIds() {
      this.originalIds = [...this.estimation.items.map(
        d => d.elevations).flat(1).map(d => d.id)];
    },
    checkElevations() {
      if (this.newElevations.length == 0) {
        this.getElevations().then(() => {
          this.initializeSpreadsheet();
        })
      } else {
        this.initializeSpreadsheet();
      }
    },
    initializeSpreadsheet() {
      this.setWidth();

      // Clear previous nodes
      let wallEl = document.getElementById("wall-spreadsheet");
      // Initialize railing spreadsheet
      let wallSpreadsheet = jexcel(
        wallEl, this.wallOptions
      );
      Object.assign(this, { wallSpreadsheet });
      wallSpreadsheet.hideColumn(0);

      // Clear previous nodes
      let railEl = document.getElementById("rail-spreadsheet");
      // Initialize railing spreadsheet
      let railSpreadsheet = jexcel(
        railEl, this.railOptions
      );
      Object.assign(this, { railSpreadsheet });
      railSpreadsheet.hideColumn(0);
    },
    updateSummary(summaryTable, rowIndex, property, v) {
      this.estimationSummary[summaryTable][rowIndex][property] = v;
    },
    formatValue(v, field) {
      if (isNaN(v)) {
        return v.replaceAll("_", " ");
      } else {
        let flds = ['unit_price', 'unit_price_adjustment', 'adjusted_unit_price', 
          'average_actual_price', 'average_value', 'system_size', 
          'adjusted_unit_price_active', 'non_active_unit_price_adj', 'active_unit_price_adj',
          'unit_price_non_active', 'unit_price_active'];
        return v ? flds.includes(field) ? this.$options.filters.commasep(v,1) : 
          this.$options.filters.commasep(v,0) : 0;
      }
    },
    getTotal(label, summary, section, isSubtotal) {
      let values = summary.map(d => d[label]);
      if (
        isNaN(values[0]) || 
        ['unit_price_adj', 'unit_price_adjustment', 'annual_sun_hours', 
          'active_unit_price_adj', 'non_active_unit_price_adj'].includes(label)
      ) {
        return '';
      } else if (
        ['unit_price', 'adjusted_unit_price', 'average_actual_price', 
          'average_value'].includes(label)
      ) {
        let numField = '';
        switch (label) {
          case 'unit_price':
            if (Object.keys(summary[0]).includes('total_cost')) {
              numField = 'total_cost';
            } else {
              numField = 'standard_quote';
            }
            break;
          case 'adjusted_unit_price':
            if (Object.keys(summary[0]).includes('adjusted_total_cost')) {
              numField = 'adjusted_total_cost';
            } else {
              numField = 'adjusted_quote';
            }
            break;
          case 'average_actual_price':
            numField = 'price';
            break;
          case 'average_value':
            numField = 'what_should_be_paid';
            break;
        }
        let price = summary.map(d => d[numField]).reduce((a, b) => a + +b, 0);
        let area = summary.map(
          d => d.sum_sqft || d.sum_area_sqft || d.sum_linear_ft || d.measure 
        ).reduce((a, b) => a + +b, 0);
        
        return this.$options.filters.commasep(price / area, 1);
      } else if (label == 'adjusted_unit_price_active') {
        let price = summary.map(d => d.adjusted_quote).reduce((a, b) => a + +b, 0);
        let area = summary.map(d => d.sum_area_sqft || d.sum_linear_ft).reduce((a, b) => a + +b, 0);

        return this.$options.filters.commasep(price / area, 1);
      } else if (label == 'unit_price_non_active') {
        let price = summary.map(d => d.non_active_price).reduce((a, b) => a + +b, 0);
        let area = summary.map(d => d.non_active_sqft || d.sum_linear_ft).reduce((a, b) => a + +b, 0);

        return this.$options.filters.commasep(price / area, 1);
      } else if (label == 'unit_price_active') {
        let price = summary.map(d => d.active_price).reduce((a, b) => a + +b, 0);
        let area = summary.map(d => d.active_sqft || d.sum_linear_ft).reduce((a, b) => a + +b, 0);

        return this.$options.filters.commasep(price / area, 1);
      } else if (label == 'system_size' && !isSubtotal) {
        let sum = this.estimationSummary[section].filter(d => 
          !d.pricing_summary.includes('batteries')
        ).map(d => d[label]).reduce((a, b) => a + +b, 0);

        return this.$options.filters.commasep(sum, 1);
      } else {
        let sum = values.reduce((a, b) => a + +b, 0);
        return this.$options.filters.commasep(sum, 0);
      }
    },
    getWallData(validate=true, obj=true) {
      const {wallFields, wallInd} = this;
      
      let wallRaw = this.wallSpreadsheet.getData();
      let wallExceptions = [wallInd.itemInd, wallInd.elevationInd];
      let wall = this.wallSpreadsheet.records.map(
        x=>x.map(y=>y.innerHTML)
      ).map((d, rowIndex) => {
        if (obj) {
          return Object.fromEntries(
            wallFields.map((_, i) => {
              if (wallExceptions.includes(i)) {
                let v = wallRaw[rowIndex][i];
                v = v == "" ? null : v.toString();
                return [wallFields[i], v];
              } else {
                let v = d[i] == "" ? null : d[i].toString(); //Don't send blank, send null instead
                return [wallFields[i], v];
              }
            })
          )
        } else {
          return wallFields.map((_, i) => {
            if (wallExceptions.includes(i)) {
              let v = wallRaw[rowIndex][i];
              v = v == "" ? null : v.toString();
              return v;
            } else {
              let v = d[i] == "" ? null : d[i].toString(); //Don't send blank, send null instead
              return v;
            }
          })
        }
      });

      if (validate) {
        return wall.filter(d => d.estimation_item_id != null && d.panels != null);
      } else {
        return wall
      }
    },
    getRailData(validate=true, obj=true) {
      const {railFields, railInd} = this;

      let railRaw = this.railSpreadsheet.getData();
      let railExceptions = [railInd.itemInd, railInd.elevationInd];
      let railing = this.railSpreadsheet.records.map(
        x=>x.map(y=>y.innerHTML)
      ).map((d, rowIndex) => {
        if (obj) {
          return Object.fromEntries(
            railFields.map((_, i) => {
              if (railExceptions.includes(i)) {
                let v = railRaw[rowIndex][i];
                v = v == "" ? null : v.toString();
                return [railFields[i], v];
              } else {
                let v = d[i] == "" ? null : d[i].toString(); //Don't send blank, send null instead
                return [railFields[i], v];
              }
            })
          )
        } else {
          return railFields.map((_, i) => {
            if (railExceptions.includes(i)) {
              let v = railRaw[rowIndex][i];
              v = v == "" ? null : v.toString();
              return v;
            } else {
              let v = d[i] == "" ? null : d[i].toString(); //Don't send blank, send null instead
              return v;
            }
          })
        }
      });

      if (validate) {
        return railing.filter(d => d.estimation_item_id != null && d.panels != null);
      } else {
        return railing
      }
    },
    updateDraft() {
      let wall = this.getWallData(false, false);
      let rail = this.getRailData(false, false);

      this.saveDraft({
        estimationId: this.$route.params.estimationId,
        data: { wall: wall, rail: rail }
      });
    },
    triggerAutoSave() {
      // Auto-save every 30 seconds
      let wall = this.getWallData(false, false);
      let rail = this.getRailData(false, false);

      this.saveDraft({
        estimationId: this.$route.params.estimationId,
        data: { wall: wall, rail: rail }
      });

      this.draftInterval = setInterval(() => {
        if (this.draftInterval) {
          this.updateDraft();
        }
      }, 30000);
    },
    loadEstimationFromDraft() {
      let wall = this.draft.data.wall;
      let rail = this.draft.data.rail;

      this.refreshTables(this.draft.data.wall, this.draft.data.rail);
      let modifiedWall = this.compareArr(wall, this.wallDetails).filter(d => typeof d != 'string');
      let modifiedRail = this.compareArr(rail, this.railDetails).filter(d => typeof d != 'string');
      this.modifiedDetails = modifiedWall.concat(modifiedRail);

      this.loadedFromDraft = true;
    },
    saveFullEstimation(wall, railing) {
      //////////////////////////////////
      // Clean up data
      //////////////////////////////////
      wall = wall ? wall : this.getWallData();
      railing = railing ? railing : this.getRailData();

      //////////////////////////////////
      // Add the order
      //////////////////////////////////
      wall.forEach((row, i) => {
        row.order = i;
      });

      railing.forEach((row, i) => {
        row.order = i + wall.length;
      });

      let newOrder = wall.concat(railing).map(
        d => { return { id: d.id, order: d.order } }
      ).filter(d => d.id != null);

      let promises = [];

      //////////////////////////////////
      // Delete
      //////////////////////////////////
      let newIds = [...wall.map(d => +d.id).concat(railing.map(d => +d.id))];
      let deleteIds = this.originalIds.filter(id => !newIds.includes(+id));
      deleteIds.forEach(deleteId => {
        promises.push(this.deleteItemElevation(deleteId));
      })

      //////////////////////////////////
      // Add
      //////////////////////////////////
      let newWallDetails = wall.filter(d => !d.id).map(d => {
        d.estimation_id = this.estimation.id;
        // d.wall_railing = "wall";
        // delete d.area_sqft;
        // delete d.cell_q;
        delete d.id;
        return d
      })
      let newRailDetails = railing.filter(d => !d.id).map(d => {
        d.estimation_id = this.estimation.id;
        // d.wall_railing = "railing";
        // delete d.area_sqft;
        // delete d.linear_in_str;
        // delete d.solar_panels;
        delete d.id;
        return d
      })

      newWallDetails.concat(newRailDetails).forEach(detail => {
        promises.push(this.addItemElevation(detail));
      })
      
      //////////////////////////////////
      // Modify
      //////////////////////////////////
      let modifyIds = this.modifiedDetails.filter(d => !deleteIds.includes(d));
      let updateWallDetails = wall.filter(
        d => !!d.id && modifyIds.includes(+d.id)
      );
      let updateRailDetails = railing.filter(
        d => !!d.id && modifyIds.includes(+d.id)
      );

      updateWallDetails.concat(updateRailDetails).forEach(detail => {
        promises.push(
          this.updateItemElevation({
            itemElevationId: detail.id,
            data: detail
          })
        )
      });

      return new Promise((resolve, reject) => {
        // Notify success
        Promise.all(promises).then(resp => {
          this.updateElevationOrders(newOrder).then(() => {
            this.refreshEstimation().then(() => {
              resolve();
            })
            this.setNotification({
              status: true,
              type: 'success',
              message: 'Estimation successfully updated'
            });
          }).catch(err => {
            this.refreshEstimation().then(() => {
              reject(err);
            });
          })
        }).catch(err => {
          this.refreshEstimation().then(() => {
            reject(err);
          })
        })
      });
    },
    refreshEstimation() {
      return new Promise((resolve, reject) => {
        this.getSummary(this.estimation.id);
        let otherPricingType = this.summaryParams.pricing_type == 'mpa' ? 'dpa' : 'mpa';
        this.getEstimationSummary({ 
          id: this.estimation.id, 
          params: { pricing_type: otherPricingType } });
        this.getEstimation(this.$route.params.estimationId).then(() => {
          this.reset();
          this.refreshTables();
          this.setSaving(false);
          resolve();
        }).catch(err => reject(err));
      })
    },
  }
}
</script>


<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.estimation__details {
  width: 100%;
  min-height: 100vh;
  overflow-x: auto;
  overflow-y: visible;
  padding: 0em 1em;
}

.jexcel_container {
  display: block;
  float: left;
}

.capitalize {
  text-transform: capitalize;
}

table .summary-table {
  th:not(:first-child), 
  td:not(:first-child) {
    text-align: right;
  }
}

.subtotal-row {
  border-bottom: 2px solid #ddd;
}
</style>
